import { Box, Drawer } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useState } from 'react';
import type { Control, UseFormHandleSubmit, UseFormSetValue } from 'react-hook-form';
import type { InteractionNotification, InteractionReport } from '@/apis/business/agent/callLogs/types';
import type { ContactCategory } from 'apis/business/agent/contact/types';
import type { JourneyItem } from './components/RightColumn/components/TranscriptToolRow';

export type { InteractionNotification };
import Header from './components/Header';
import LeftColumn from './components/LeftColumn';
import MiddleColumn from './components/MiddleColumn';
import RightColumn from './components/RightColumn';
import ReportCallDrawer from './components/ReportCallDrawer';

export type InteractionLogFormView = 'business' | 'agency-call-logs' | 'agency-chat-logs' | 'agency-system-sentiment';

export type CallLogDisplay = {
  id?: number;
  channel?: string;
  direction?: 'inbound' | 'outbound';
  interaction_type?: 'call' | 'chat';
  start_at?: string;
  disconnection_reason?: string | null;
  recording_url?: string;
  summary?: string;
  interaction_journey?: JourneyItem[];
  email_logs?: InteractionNotification[];
  sms_logs?: InteractionNotification[];
  details?: {
    session_id?: string;
    from_phone?: string;
    to_phone?: string;
    duration?: number;
  };
  report_call?: InteractionReport | null;
};

export type ContactDisplay = {
  phone_number?: string | null;
  category?: ContactCategory;
  chats_count?: number;
  calls_count?: number;
};

export type AssociatedRecordDisplay = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  external_id?: string;
  phone_number1: string;
  phone_number2?: string;
  phone_number3?: string;
  dob?: string;
  created_at?: string;
  updated_at?: string;
};

export type NoteDisplay = {
  id: number;
  description: string;
  created_at: string;
};

export type SignalDisplay = {
  sentiment?: string;
  severity?: string;
  keywords?: string[];
  workflows?: string[];
  tasks_completed?: Record<string, number | boolean> | string[];
  audit_issues?: { label: string; description: string }[];
};

export type FormValues = {
  contact: ContactDisplay & { call: number | string };
  note: { description: string };
};

export type InteractionLogFormProps = {
  view: InteractionLogFormView;
  isAgencyView: boolean;
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  callLog?: CallLogDisplay;
  callLogId?: string;
  contact?: ContactDisplay;
  contactId?: number;
  associatedRecords?: { integration_entity_contacts?: AssociatedRecordDisplay[] };
  notes?: { data: NoteDisplay[] };
  control: Control<FormValues>;
  handleSubmit: UseFormHandleSubmit<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  editingNote: number | null;
  handleSelectCallLog: (id: number | undefined) => void;
  handleCreateOrUpdateNote: (data: { note: { description: string } }) => Promise<void>;
  handleEditClick: (note: { id: number; description: string }) => void;
  deleteNote: (id: number) => void;
  downloadTranscript: () => void;
  isCreatePending: boolean;
  isUpdatePending: boolean;
  signalData: SignalDisplay;
  endReason?: string;
  endReasonLabel?: string;
  keywords: string[];
  workflows: string[];
  taskCompleted: string[];
  systemSentiments?: { label: string; description: string }[];
  agentName: string;
  businessName: string;
  formatLogDate: (date: string) => string;
  formatNoteDate: (date: string) => string;
  formatTranscriptTimestamp: (timestamp?: number) => string;
  showInteractionTypeFilter?: boolean;
};

function getLayoutSx() {
  const columnScrollSx = {
    height: { xs: 'auto', lg: '100%' },
    minHeight: 0,
    overflowY: { xs: 'visible', lg: 'auto' },
    overflowX: 'hidden'
  } as const;

  const leftColumnSx = {
    ...columnScrollSx,
    pr: 1,
    bgcolor: '#F8F9FA',
    borderRadius: '8px',
    padding: 2
  };

  const middleColumnSx = { ...columnScrollSx, pr: 1 };

  const bodySx = {
    flex: 1,
    overflowY: { xs: 'auto', lg: 'hidden' },
    overflowX: 'hidden',
    p: { xs: 2, lg: 3 },
    minHeight: 0
  } as const;

  const gridSx = { height: { xs: 'auto', lg: '100%' }, minHeight: 0, alignItems: 'stretch' };

  return { leftColumnSx, middleColumnSx, bodySx, gridSx };
}

export default function InteractionLogForm({ isAgencyView, isOpen, onClose, ...columnProps }: InteractionLogFormProps) {
  const { leftColumnSx, middleColumnSx, bodySx, gridSx } = getLayoutSx();
  const [reportOpen, setReportOpen] = useState(false);

  return (
    <>
      <Drawer
        anchor='right'
        open={isOpen}
        ModalProps={{ keepMounted: true }}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 600, md: 800, lg: 1400 },
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Header callLog={columnProps.callLog} contact={columnProps.contact} onClose={onClose} />

        <Box sx={bodySx}>
          <Grid container spacing={2} sx={gridSx}>
            <LeftColumn
              isAgencyView={isAgencyView}
              isLoading={columnProps.isLoading}
              leftColumnSx={leftColumnSx}
              agentName={columnProps.agentName}
              businessName={columnProps.businessName}
              signalData={columnProps.signalData}
              keywords={columnProps.keywords}
              workflows={columnProps.workflows}
              taskCompleted={columnProps.taskCompleted}
              systemSentiments={columnProps.systemSentiments}
            />
            <MiddleColumn
              isLoading={columnProps.isLoading}
              middleColumnSx={middleColumnSx}
              associatedRecords={columnProps.associatedRecords}
              notes={columnProps.notes}
              formatNoteDate={columnProps.formatNoteDate}
              control={columnProps.control}
              handleSubmit={columnProps.handleSubmit}
              editingNote={columnProps.editingNote}
              handleEditClick={columnProps.handleEditClick}
              deleteNote={columnProps.deleteNote}
              handleCreateOrUpdateNote={columnProps.handleCreateOrUpdateNote}
              isCreatePending={columnProps.isCreatePending}
              isUpdatePending={columnProps.isUpdatePending}
            />
            <RightColumn
              isLoading={columnProps.isLoading}
              isOpen={isOpen}
              callLog={columnProps.callLog}
              control={columnProps.control}
              contactId={columnProps.contactId}
              chatsCount={columnProps.contact?.chats_count}
              callsCount={columnProps.contact?.calls_count}
              isAgencyView={isAgencyView}
              formatLogDate={columnProps.formatLogDate}
              endReason={columnProps.endReason}
              endReasonLabel={columnProps.endReasonLabel}
              handleSelectCallLog={columnProps.handleSelectCallLog}
              setValue={columnProps.setValue}
              formatTranscriptTimestamp={columnProps.formatTranscriptTimestamp}
              downloadTranscript={columnProps.downloadTranscript}
              showInteractionTypeFilter={columnProps.showInteractionTypeFilter}
              report={columnProps.callLog?.report_call}
              onReportClick={() => setReportOpen(true)}
            />
          </Grid>
        </Box>
      </Drawer>
      <ReportCallDrawer
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        interactionId={columnProps.callLog?.id}
        sessionId={columnProps.callLog?.details?.session_id}
        report={columnProps.callLog?.report_call}
        readOnly={isAgencyView}
      />
    </>
  );
}
